/**
 * @fileoverview Canonical template-scope rule engine. Evaluates template-level
 *               rules (cross-form hide/show, cross-form require) against the
 *               current flat-value map produced by the form renderer. Output
 *               shape maps 1:1 onto the canonical `FormVisibilityState` /
 *               `SectionVisibilityState` so canvas preview, tenant-web
 *               inspection completion, and mobile-pwa inspection completion
 *               all compose the identical `formStates{}` / `sectionStates{}`
 *               maps on the live `InspectionCase`.
 * @module @firesquirrel/core-domain/inspection/templateRuleEngine
 *
 * Responsibilities:
 * - Evaluate `InspectionRule[]` at template scope across all forms.
 * - Apply `hideForm` / `showForm` / `hideSection` / `showSection` /
 *   `hideField` / `showField` / `require` actions to a single combined result.
 * - Re-use the leaf / compound condition evaluator from `rulesEngine.ts` so
 *   form-scope and template-scope evaluation can NEVER diverge.
 * - Provide a helper to build a blank `AllFormValues` map from a set of forms
 *   with their default values applied.
 * - Provide a helper that filters an ordered `FormRef[]` by the hidden-forms
 *   set and returns the visible form IDs in display order.
 *
 * Dependencies:
 * - `./rulesEngine` — `evaluateCondition` (shared leaf evaluator).
 * - `./types` — `Condition`, `RuleAction`, `InspectionRule`, `InspectionForm`,
 *   `FormRef`, `FieldResponse`.
 *
 * Architecture:
 * - Template-scope rules are authored in the shared `@ui-admin`
 *   `CanvasRulesPanel` and stored on `template.rules`. Form-scope rules live
 *   on `form.rules` and are evaluated by `evaluateFormRules`. The two scopes
 *   share the same `Condition` / `RuleAction` grammar so the evaluator kernel
 *   is identical.
 * - The engine is pure: given the same rules + values it always produces the
 *   same result. This guarantees canvas-preview and inspection-completion
 *   replay parity (see `Docs/implementation-plans/canonical-response-model-plan.md`).
 *
 * Notes:
 * - `AllFormValues` is the raw-value representation produced by the
 *   form renderer's session state (`Record<formId, Record<fieldId, unknown>>`).
 *   Inside the evaluator it is flattened to a `Record<string, FieldResponse>`
 *   shape so `evaluateCondition` can be reused verbatim — the flattening
 *   picks the first occurrence of a `fieldId` across forms (fieldIds are
 *   globally unique within a template by contract).
 */

import { evaluateCondition } from './rulesEngine';
import type {
  Condition,
  FieldResponse,
  FormRef,
  InspectionForm,
  InspectionRule,
  RuleAction,
} from './types';

// ---------------------------------------------------------------------------
// Public types — `AllFormValues` is the session-state map the renderer holds;
// `TemplateRuleEvalResult` is the combined effect set template-scope rules
// produce.
// ---------------------------------------------------------------------------

/**
 * @contract AllFormValues
 *
 * Purpose:
 * - Represents the renderer's session state: each form's fields keyed by
 *   `fieldId` to their current raw values (primitive or array).
 *
 * Fields:
 * - [formId]: Record<fieldId, unknown> — per-form field values.
 *
 * Rules:
 * - `fieldId` is globally unique within a template.
 *
 * Sync:
 * - Local-only. Persisted form results are written through the canonical
 *   response model (`CanonicalQuestionResponse`) — `AllFormValues` is only
 *   the live session state driving the renderer.
 */
export type AllFormValues = Record<string, Record<string, unknown>>;

/**
 * @contract TemplateRuleEvalResult
 *
 * Purpose:
 * - Combined effect set produced by evaluating template-scope rules against
 *   an `AllFormValues` snapshot.
 *
 * Fields:
 * - hiddenForms: Set<string> — formIds the rules want hidden.
 * - shownForms: Set<string> — formIds the rules have explicitly shown
 *   (overrides a default-hidden form).
 * - hiddenSections: Set<string> — sectionIds across all forms.
 * - hiddenFields: Set<string> — fieldIds across all forms.
 * - requiredFields: Set<string> — fieldIds the rules have promoted to
 *   required.
 * - shownSections / shownFields: sectionIds and fieldIds explicitly shown
 *   by matching rules.
 *
 * Rules:
 * - `show*` overrides `hide*` for the same entity (last-writer-wins per
 *   entity; show wins when both a show and a hide match).
 *
 * Sync:
 * - Pure computed value; never persisted. The canonical
 *   `formStates{}` / `sectionStates{}` on the `InspectionCase` are the
 *   persisted artifacts — they are derived from this result at capture
 *   time by `buildEventResponseSnapshot`.
 */
export interface TemplateRuleEvalResult {
  hiddenForms: Set<string>;
  shownForms: Set<string>;
  hiddenSections: Set<string>;
  hiddenFields: Set<string>;
  requiredFields: Set<string>;
  shownSections: Set<string>;
  shownFields: Set<string>;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Flatten `AllFormValues` into the `Record<string, FieldResponse>` shape
 * consumed by `evaluateCondition`. FieldIds are globally unique within a
 * template by contract; first occurrence wins if a duplicate ever appears.
 */
function flattenToResponses(all: AllFormValues): Record<string, FieldResponse> {
  const flat: Record<string, FieldResponse> = {};
  for (const fields of Object.values(all)) {
    for (const [fieldId, value] of Object.entries(fields)) {
      if (fieldId in flat) continue;
      flat[fieldId] = { value };
    }
  }
  return flat;
}

/**
 * Apply a single rule action into the accumulated result.
 */
function applyTemplateAction(action: RuleAction, out: TemplateRuleEvalResult): void {
  switch (action.action) {
    case 'hideForm':
      if (!out.shownForms.has(action.formId)) {
        out.hiddenForms.add(action.formId);
      }
      break;
    case 'showForm':
      out.shownForms.add(action.formId);
      out.hiddenForms.delete(action.formId);
      break;
    case 'hideSection':
      if (!out.shownSections.has(action.sectionId)) {
        out.hiddenSections.add(action.sectionId);
      }
      break;
    case 'showSection':
      out.shownSections.add(action.sectionId);
      out.hiddenSections.delete(action.sectionId);
      break;
    case 'hideField':
      if (!out.shownFields.has(action.fieldId)) {
        out.hiddenFields.add(action.fieldId);
      }
      break;
    case 'showField':
      out.shownFields.add(action.fieldId);
      out.hiddenFields.delete(action.fieldId);
      break;
    case 'require':
      out.requiredFields.add(action.fieldId);
      break;
    case 'setOptions':
      // Option-mutation is handled per-field by the form-scope engine; no
      // template-scope effect to record here.
      break;
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * @process evaluateTemplateRules
 *
 * Steps:
 * 1. Flatten `AllFormValues` into a `FieldResponse` map.
 * 2. Walk every rule in order; when `rule.when` matches, apply each
 *    action into the accumulated result.
 * 3. Return the combined effect set.
 *
 * Inputs:
 * - rules: the template's authored rule list (typically `template.rules`).
 * - allValues: the renderer's current session-state values.
 *
 * Outputs:
 * - `TemplateRuleEvalResult` with hidden/shown/required sets.
 *
 * Failure Handling:
 * - Individual rule evaluation errors are swallowed so one malformed rule
 *   cannot break the whole pass. Errors are NEVER logged here — logging
 *   is the caller's responsibility.
 *
 * Idempotency:
 * - REQUIRED. Same inputs always yield the same `TemplateRuleEvalResult`.
 */
export function evaluateTemplateRules(
  rules: readonly InspectionRule[] | readonly { when: Condition; then: RuleAction[] }[] | undefined | null,
  allValues: AllFormValues,
): TemplateRuleEvalResult {
  const result: TemplateRuleEvalResult = {
    hiddenForms: new Set<string>(),
    shownForms: new Set<string>(),
    hiddenSections: new Set<string>(),
    hiddenFields: new Set<string>(),
    requiredFields: new Set<string>(),
    shownSections: new Set<string>(),
    shownFields: new Set<string>(),
  };

  if (!rules || rules.length === 0) return result;

  const responses = flattenToResponses(allValues);

  for (const rule of rules) {
    try {
      if (evaluateCondition(rule.when, responses)) {
        for (const action of rule.then) {
          applyTemplateAction(action, result);
        }
      }
    } catch {
      // Swallow — see failure-handling note above.
    }
  }

  return result;
}

/**
 * @process buildBlankValues
 *
 * Steps:
 * 1. For each form, seed a per-form `Record<fieldId, unknown>` with any
 *    declared `defaultValue` from the field definition.
 * 2. Return the assembled `AllFormValues`.
 *
 * Inputs:
 * - forms: either an array or a Map of `InspectionForm` objects.
 *
 * Outputs:
 * - A fresh `AllFormValues` with defaults pre-populated.
 *
 * Idempotency:
 * - REQUIRED.
 *
 * Notes:
 * - The form renderer applies `today` / `now` default-value sentinels at
 *   mount time — they are left as-is here so the caller's persistence
 *   path sees the same raw defaults the renderer will resolve.
 */
export function buildBlankValues(
  forms: ReadonlyArray<InspectionForm> | ReadonlyMap<string, InspectionForm>,
): AllFormValues {
  const out: AllFormValues = {};
  const iter: Iterable<InspectionForm> =
    forms instanceof Map ? forms.values() : (forms as Iterable<InspectionForm>);
  for (const form of iter) {
    const bucket: Record<string, unknown> = {};
    for (const field of form.fields) {
      if (field.defaultValue !== undefined) {
        bucket[field.id] = field.defaultValue;
      }
    }
    out[form._id] = bucket;
  }
  return out;
}

/**
 * @process getVisibleFormIds
 *
 * Steps:
 * 1. Sort the given form refs by `order`.
 * 2. Filter out any formId that is in `result.hiddenForms` AND not in
 *    `result.shownForms`.
 * 3. Return the resulting formIds in display order.
 *
 * Inputs:
 * - formRefs: the template's `formRefs` array (ordered form references).
 * - result: the `TemplateRuleEvalResult` produced by `evaluateTemplateRules`.
 *
 * Outputs:
 * - Ordered array of formIds that should be visible in the renderer.
 *
 * Idempotency:
 * - REQUIRED.
 */
export function getVisibleFormIds(
  formRefs: ReadonlyArray<FormRef>,
  result: TemplateRuleEvalResult,
): string[] {
  return [...formRefs]
    .sort((a, b) => a.order - b.order)
    .filter((ref) => !result.hiddenForms.has(ref.formId) || result.shownForms.has(ref.formId))
    .map((ref) => ref.formId);
}
